<style scoped lang="scss">
.financial-step {
  padding: 10px 25px;
  align-items: center;

  .icon-container {
    margin-right: 20px;
  }

  .document-title {
    font-size: 1.2em;
    margin-bottom: 2px;
  }

  .details {
    color: #666;
  }
}
.uk-text-smaller {
  font-size: 0.65em;
}
</style>

<template>
  <div class="uk-container financial-step" v-if="user">
    <div class="uk-grid" >
      <div class="uk-width-small-1-1 uk-width-medium-1-2 uk-margin-large-bottom" v-if="programs.length > 1 && !program">
        <p><small>Please select the primary program you will be utilizing</small></p>
        <select :value="programId" @change="$store.commit('Project/SET_PROGRAM_ID', $event.target.value)" class="uk-select uk-width-1-1">
          <option v-for="_program in programs" :value="_program.id">{{ _program.program_name }}</option>
        </select>
      </div>
    </div>
    <ValidationObserver
        v-slot="{ handleSubmit, invalid, dirty }"
        v-if="program"
        ref="financialForm"
    >
      <form class="uk-form" @submit.prevent="handleSubmit(create)" autocomplete="chrome-off">
        <div class="uk-grid uk-margin-large-top uk-margin-large-bottom">

          <div class="uk-width-1-1">
            <h2>Which company will receive funds and the 1099 from us?</h2>
            <select :value="project.payee_organization_id"
                    @change="updatePayeeId"
                    class="uk-form-large uk-select">
              <option :value="null">Select a Payee Company</option>
              <option :value="ownerOrganization.id">{{ ownerOrganization.organization_name }}</option>
              <option v-if="managerOrganization && managerOrganization.id != ownerOrganization.id" :value="managerOrganization.id">{{ managerOrganization.organization_name }}</option>
            </select>
          </div>

          <div v-if="payeeOrganization" class="uk-width-1-1 uk-width-medium-1-2 uk-margin-small-top">
            <p class="uk-margin-bottom-remove"><small>&nbsp;Enter the tax id or social security number of the Company</small></p>
            <validated-section :errors="errors" field="tax_id_number">
              <input type="text"
                     v-if="maskedTaxIdNumber"
                     :value="maskedTaxIdNumber"
                     class="uk-form-large uk-width-1-1"
                     disabled="disabled"
                     placeholder="Tax ID Number"
              >
              <input type="text"
                     v-else
                     v-model="taxIdNumber"
                     class="uk-form-large uk-width-1-1"
                     v-mask="'#########'"
                     placeholder="Tax ID Number"
              >
            </validated-section>
          </div>


        </div>
        <div v-if="payeeOrganization" class="uk-grid uk-margin-large-top uk-margin-large-bottom">
          <div class="uk-align-center uk-width-small-1-1 uk-width-medium-5-5">
            <div class="uk-grid renter-info">
              <div class="uk-width-1-1" v-if="documents.length === 0">
                <h2>Please upload a W9 for payment reimbursement</h2>
              </div>
              <div class="uk-width-1-1 uk-margin-top uk-margin-bottom" v-if="documentsOfType('landlord-w9').length === 0">
                <p style="font-size: 1.2em;">
                  You can download a copy of the current W9 file from the IRS
                </p>
                <label class="custom-file-upload uk-button uk-width-1-2 uk-align-center">
                  <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank">
                    <span class="a-download-2"></span> &nbsp;DOWNLOAD W9 FORM
                  </a>
                </label>
              </div>

              <div
                  v-for="document in documentsOfType('landlord-w9')"
                  class="uk-width-1-1 uk-grid uk-margin-large-top"
              >
                <div class="uk-width-1-1 document-container uk-flex uk-flex-space-between">
                  <div class="uk-flex uk-center">
                    <div class="icon-container">
                      <a href="javascript:;" @click="previewFile(document)">
                        <i class="uk-icon-medium a-file-search"></i><br>
                        <span style="font-size: 0.8em;">REVIEW</span>
                      </a>
                    </div>
                    <div>
                      <div class="document-title">{{ document.filename }}</div>
                      <div class="details attention">
                        Uploaded {{ document.created_at | moment }}<br />
                        <strong>NOTE: ANY MEMBER OF YOUR ORGANIZATION WITH ACCESS TO THIS SITE IS ABLE TO REVIEW THIS SENSITIVE INFORMATION. PROVIDE ACCESS WITH CARE.</strong>
                      </div>
                    </div>
                    <div>
                      <a href="javascript:;" class="uk-button uk-button-danger uk-margin-left" @click="deleteDocument(document)"><i class="fa fa-trash"></i></a>
                    </div>
                  </div>
                </div>
              </div>

              <div class="uk-width-1-1 uk-margin-top uk-padding-remove" v-if="documentsOfType('landlord-w9').length === 0">
                <validated-section field="w9" :errors="errors">
                  <file-upload-row
                      ref="fileUpload"
                      accepts="application/pdf,image/*"
                      :upload-url="'/api/projects/' + projectId + '/document/upload'"
                      @uploaded="onUploaded"
                      :params="{organization_id: payeeOrganization.id}"
                      fileType="landlord-w9"
                  />
                </validated-section>
              </div>
            </div>
            <div class="uk-grid">
              <div class="uk-width-1-1 uk-width-medium-1-2 uk-width-small-1-1">
                <h3>Payment Method</h3>
                <validated-section :errors="errors" field="payment_method">
                  <select v-model="paymentType" class="uk-width-small-1-1 uk-form-select uk-form-large">
                    <option :value="null">Please Select a Payment Method...</option>
                    <option v-for="(label, value) in program.setting_details['organization-payment-types'].options"
                            v-if="hasPaymentType(value)"
                            :value="value"
                    >{{ label }}</option>
                  </select>
                </validated-section>

              </div>
            </div>
            <template v-if="paymentType == 2">
              <div class="uk-grid">

                <div class="uk-width-small-1-1 uk-width-medium-1-2 uk-margin-large-bottom">
                  <p class="uk-margin-bottom"><small>&nbsp;Account Type</small></p>
                  <validated-section :errors="errors" field="bank_account_type">
                    <div class="uk-grid">
                      <div class="uk-width-1-2">
                        <label for="bank_account_type">
                          <input type="radio" name="bank_account_type" v-model="accountType" :value="1">
                          Checking
                        </label>
                      </div>

                      <div class="uk-width-1-2">
                        <label for="bank_account_type">
                          <input type="radio" name="bank_account_type" v-model="accountType" :value="2">
                          Savings
                        </label>
                      </div>
                    </div>
                  </validated-section>
                </div>

                <div class="uk-width-small-1-1 uk-width-medium-1-2">
                  &nbsp;
                </div>



                <div class="uk-width-1-1 uk-width-medium-1-2 uk-width-small-1-1">
                  <p class="uk-margin-bottom-remove"><small>&nbsp;Routing Number</small></p>
                  <input type="text"
                         v-if="maskedRoutingNumber"
                         :value="maskedRoutingNumber"
                         class="uk-form-large uk-width-1-1"
                         disabled="disabled"
                  >
                  <input type="text"
                         class="uk-form-large uk-width-1-1"
                         v-else
                         v-mask="'#########'"
                         v-model="routingNumber"/>
                </div>
                <div class="uk-width-1-1 uk-width-medium-1-2 uk-width-small-1-1">
                  <p class="uk-margin-bottom-remove"><small>&nbsp;Routing Number Confirmation</small></p>
                  <input type="text"
                         v-if="maskedRoutingNumber"
                         :value="maskedRoutingNumber"
                         class="uk-form-large uk-width-1-1"
                         disabled="disabled"
                  >
                  <input type="text"
                         v-else
                         class="uk-form-large uk-width-1-1"
                         v-mask="'#########'"
                         v-model="routingNumberConfirmation" />
                </div>
                <div class="uk-width-1-1">
                  <validated-section field="routing" :errors="errors"></validated-section>
                </div>
              </div>

              <div class="uk-grid">
                <div class="uk-width-1-1 uk-width-medium-1-2 uk-width-small-1-1">
                  <p class="uk-margin-bottom-remove"><small>&nbsp;Account Number</small></p>
                  <input type="text"
                         v-if="maskedAccountNumber"
                         :value="maskedAccountNumber"
                         class="uk-form-large uk-width-1-1"
                         disabled="disabled"
                  >
                  <input type="text"
                         v-else
                         class="uk-form-large uk-width-1-1"
                         v-mask="'#####################'"
                         v-model="accountNumber"
                  >
                </div>
                <div class="uk-width-1-1 uk-width-medium-1-2 uk-width-small-1-1">
                  <p class="uk-margin-bottom-remove"><small>&nbsp;Account Number Confirmation</small></p>
                  <input type="text"
                         v-if="maskedAccountNumber"
                         :value="maskedAccountNumber"
                         class="uk-form-large uk-width-1-1"
                         disabled="disabled"
                  >
                  <input type="text"
                         v-else
                         class="uk-form-large uk-width-1-1"
                         v-mask="'#####################'"
                         v-model="accountNumberConfirmation"
                  />
                </div>
                <div class="uk-width-1-1">
                  <validated-section field="account" :errors="errors"></validated-section>
                </div>

                <div class="uk-width-1-1" v-if="documentsOfType('void_check').length > 0">
                  <h2>Voided Check Image</h2>
                  <div v-for="document in documentsOfType('void_check')"
                       class="uk-width-1-1 uk-grid uk-margin-large-top">
                    <div class="uk-width-1-1 document-container uk-flex uk-flex-space-between">
                      <div class="uk-flex uk-center">
                        <div class="icon-container">
                          <a href="javascript:;" @click="previewFile(document)">
                            <i class="uk-icon-medium a-file-search"></i><br>
                            <span style="font-size: 0.8em;">REVIEW</span>
                          </a>
                        </div>
                        <div>
                          <div class="document-title">{{ document.filename }}</div>
                          <div class="details attention">
                            Uploaded {{ document.created_at | moment }}<br />
                            <strong>NOTE: ANY MEMBER OF YOUR ORGANIZATION WITH ACCESS TO THIS SITE IS ABLE TO REVIEW THIS SENSITIVE INFORMATION. PROVIDE ACCESS WITH CARE.</strong>
                          </div>
                        </div>
                        <div>
                          <a href="javascript:;" class="uk-button uk-button-danger uk-margin-left" @click="deleteDocument(document)"><i class="fa fa-trash"></i></a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="uk-width-1-1 uk-margin-top" v-else>
                  <h2>Voided Check Image</h2>
                  <validated-section field="check" :errors="errors">
                    <file-upload-row
                        ref="checkFileUpload"
                        accepts="application/pdf,image/png,image/jpg,image/jpeg"
                        :upload-url="'/api/projects/' + projectId + '/document/upload'"
                        @uploaded="onUploaded"
                        :params="{organization_id: payeeOrganization.id}"
                        fileType="void_check"
                    />
                  </validated-section>
                </div>
              </div>
            </template>
          </div>
        </div>
      </form>
    </ValidationObserver>
    <preview-document-modal ref="previewModal"></preview-document-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import FileUploadRow from "../../../../../components/FileUploadRow";
import ValidatedSection from "../../../../components/Common/ValidatedSection";
import PreviewDocumentModal from "../../../Documents/PreviewDocumentModal";

export default {
  name: "FinancialInformation",
  components: {PreviewDocumentModal, ValidatedSection, FileUploadRow},
  data() {
    return {
      paymentType: null,
      accountType: null,
      maskedRoutingNumber: '',
      routingNumber: '',
      routingNumberConfirmation: '',
      maskedAccountNumber: '',
      accountNumber: '',
      accountNumberConfirmation: '',
      newProjectName: '',
      maskedTaxIdNumber: '',
      taxIdNumber: '',
      application: {},
      errors: {},
    }
  },
  mounted() {
  },
  watch: {
    payeeOrganization(newValue) {
      console.log('setting payee info from ', newValue);
      if(newValue) {
        this.paymentType = newValue.payment_type;
        this.accountType = newValue.bank_account_type;
        this.maskedRoutingNumber = newValue.masked_routing_number;
        this.maskedAccountNumber = newValue.masked_account_number;
        this.maskedTaxIdNumber = newValue.masked_tax_id_number;
        this.$store.dispatch('Project/fetchOrgDocument', newValue.id);
      }
    },
    program(newProgram) {
      if(newProgram) {
        if (newProgram.setting_details['organization-payment-types'].options)
        {
          if (newProgram.settings['organization-payment-types'] && newProgram.settings['organization-payment-types'].length)
          {
            let index = parseInt(newProgram.settings['organization-payment-types'][0])
            this.$store.dispatch('Project/updateOwnerValue', {field: 'payment_type', value: index})
            console.log('xxx', index)
          }
        }

      }
    },
  },
  computed: {

    ...mapState('App', {
      user: state => state.user,
      permissions: state => state.permissions,
    }),

    ...mapState('Project', {
      project: state => state.project,
      taxId: state => state.taxId,
      ownerOrganization: state => state.ownerOrganization,
      managerOrganization: state => state.managerOrganization,
      paymentType: state => state.ownerOrganization.payment_type,
      programs: state => state.programs,
      programId: state => state.programId,
      documents: state => state.documents,
    }),

    ...mapGetters('Project', [
          'payeeOrganization',
          'program',
    ]),

    projectId() {
      return this.project ? this.project.id : undefined
    },
  },
  methods: {
    hasPaymentType(value) {
      return _.includes(this.program.settings['organization-payment-types'], value);
    },
    documentsOfType(type) {
      return _.filter(this.documents, doc => {
        return doc.file_type === type;
      });
    },
    changePaymentType($event) {
      this.$store.dispatch('Project/updateOwnerValue', {field: 'payment_type', value: parseInt($event.target.value)})
      window.setTimeout(this.$forceUpdate, 200);
    },
    setOwnerValue(field, value) {
      this.$store.dispatch('Project/updateOwnerValue', {field,value});
      window.setTimeout(this.$forceUpdate, 500);
    },
    reset() {
      this.paymentMethod = {
        type: 1,
        routingNumber: null,
        routingNumberConfirmation: null,
        accountNumber: null,
        accountNumberConfirmation: null,
        bankAccountType: null,
      }
      this.application = {}
      this.errors = {}
      if(this.$refs.fileUpload) {
        this.$refs.fileUpload.reset();
        this.$refs.checkFileUpload.reset();
      }
    },
    onUploaded($event) {
      this.$store.dispatch('Project/fetchOrgDocument', this.payeeOrganization.id);
    },

    async validateStep() {
      let stepValid = false

      this.errors = {};

      if(this.documents.length === 0) {
        this.errors['w9'] = { errors: ['You must upload at least one W9 document to continue.'] };
      }

      if(this.paymentType === null) {
        this.errors['payment_method'] = { errors: ['You must select a payment type.'] }
      }

      if((!this.taxIdNumber || this.taxIdNumber.length !== 9) && !this.maskedTaxIdNumber) {
          this.errors['po_organization.tax_id_number'] = ['You must have a tax id in the format of #########']
      }

      if(this.paymentType == 2) {

        if(this.documentsOfType('void_check').length === 0) {
          this.errors['check'] = { errors: ['You must upload an image of a voided check to continue.'] };
        }

        if(!this.routingNumber && !this.maskedRoutingNumber) {
          this.errors['routing'] = { errors: ['The Routing Number is required.'] };
        }

        if(this.accountType === undefined) {
          this.errors['bank_account_type'] = { errors: ['You must select an account type.'] };
        }

        if(!this.accountNumber && !this.maskedAccountNumber ) {
          this.errors['account'] = { errors: ['The Account Number is required.'] };
        }
      }

      if (this.routingNumber !== this.routingNumberConfirmation)
      {
        this.errors['routing'] = { errors: ['Routing Number Confirmation doesn\'t match.'] };
      }

      if (this.accountNumber !== this.accountNumberConfirmation)
      {
        this.errors['account'] = { errors: ['Account Number Confirmation doesn\'t match.'] };
      }

      let size = _.size(this.errors);

      if(size > 0) return false;

      await this.$refs.financialForm.validate().then(async (isValid) => {
        if (isValid) {
          stepValid = await this.submitForm()
        }
      })

      return stepValid
    },

    async submitForm() {
      let stepValid = false

      this.$emit('loading', true)
      const payload = {
        id: this.projectId,
        param: {
          payment_on_file: this.paymentInformationComplete,
          payment_type: this.paymentType,
          account_type: this.accountType,
          routing_number: this.maskedRoutingNumber ? -1 : this.routingNumber,
          routing_number_confirmation: this.maskedRoutingNumber ? -1 : this.routingNumberConfirmation,
          account_number: this.maskedAccountNumber ? -1 : this.accountNumber,
          account_number_confirmation: this.maskedAccountNumber ? -1 : this.accountNumberConfirmation,
          project_id: this.project.id,
          tax_id_number: this.maskedTaxIdNumber ? -1 :this.taxIdNumber,
          is_renter_same_manager: this.application.is_renter_same_manager,
          payee_organization_id: this.project.payee_organization_id,
        }
      }

      await this.$store
          .dispatch('Project/update', payload)
          .then((res) => {
            this.$emit('done', res)
            this.$emit('loading', false)
            stepValid = true
          })
          .catch((err) => {
            this.$emit('loading', false)
            stepValid = false
          })

      return stepValid
    },

    previewFile(document) {
      let url =`/api/projects/${this.projectId}/document/${document.id}/preview`;
      this.$refs.previewModal.open(url, document);

    },

    deleteDocument(document) {
      const param = {
        projectId: this.projectId,
        documentId: document.id
      }
      this.$store.dispatch('Project/deleteDocument', param);
    },

    updatePayeeId($event) {
      console.log($event);
      this.$store.dispatch('Project/setPayeeOrganizationId', $event.target.value);
    }
  }
}
</script>
